// screens/SiteDocumentsAdminScreen.js
//
// HADACY · admin · documentos legales
//
// Lets admins upload / replace / delete the three legal PDFs shown in the
// footer and on the public document pages:
//   - Aviso de Privacidad
//   - Lineamientos para distribuidores
//   - Términos y Condiciones
//
// Follows the same client-direct Cloudinary flow used by the site-images
// admin: the PDF is uploaded straight to Cloudinary with the unsigned
// "HADACY" preset, then the resulting metadata is persisted via the
// site-documents API.

import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Loader from "../components/Loader";
import {
  fetchSiteDocuments,
  upsertSiteDocument,
  deleteSiteDocument,
} from "../actions/siteDocumentActions";

// Uploads PDFs (resource_type auto) with the same unsigned preset / cloud as
// the site-images admin.
const CLOUDINARY_UPLOAD_URL =
  "https://api.cloudinary.com/v1_1/cc1991/auto/upload";
const CLOUDINARY_PRESET = "HADACY";

// The fixed set of legal documents. Adding another one later is just a row.
const SLOTS = [
  {
    key: "aviso_privacidad",
    label: "Aviso de Privacidad",
    where:
      "Se muestra en el pie de página como 'Política de Privacidad' y en el registro.",
  },
  {
    key: "lineamientos_distribuidores",
    label: "Lineamientos para distribuidores",
    where: "Documento interno para distribuidores.",
  },
  {
    key: "terminos_condiciones",
    label: "Términos y Condiciones",
    where:
      "Se muestra en el pie de página como 'Términos y Condiciones' y en el registro.",
  },
];

const fmtMB = (bytes) => (bytes / (1024 * 1024)).toFixed(2);

const fmtDate = (v) =>
  v
    ? new Date(v).toLocaleString("es-MX", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "—";

const MAX_MB = 15;
const MAX_BYTES = MAX_MB * 1024 * 1024;

const SiteDocumentsAdminScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { userInfo } = useSelector((s) => s.userLogin);
  const { loading, error, documents } = useSelector((s) => s.siteDocuments);

  const [uploadingKey, setUploadingKey] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", tone: "ok" });

  const showToast = (message, tone = "ok") =>
    setToast({ show: true, message, tone });

  useEffect(() => {
    if (!toast.show) return undefined;
    const t = setTimeout(
      () => setToast((prev) => ({ ...prev, show: false })),
      3500
    );
    return () => clearTimeout(t);
  }, [toast.show]);

  useEffect(() => {
    if (!userInfo || !userInfo.isAdmin) {
      navigate("/login");
      return;
    }
    dispatch(fetchSiteDocuments());
  }, [dispatch, navigate, userInfo]);

  const docsByKey = useMemo(() => {
    const m = {};
    (documents || []).forEach((d) => {
      if (d && d.key) m[d.key] = d;
    });
    return m;
  }, [documents]);

  const uploadToCloudinary = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("upload_preset", CLOUDINARY_PRESET);

    const res = await fetch(CLOUDINARY_UPLOAD_URL, {
      method: "POST",
      body: fd,
    });

    if (!res.ok) {
      let details = "";
      try {
        const txt = await res.text();
        details = txt;
        const maybe = JSON.parse(txt);
        if (maybe?.error?.message) details = maybe.error.message;
      } catch {}
      throw new Error(`Cloudinary: ${details || `HTTP ${res.status}`}`);
    }
    return await res.json();
  };

  const onPickFile = async (slot, file) => {
    if (!file) return;

    if (file.type !== "application/pdf") {
      showToast("El archivo debe ser un PDF.", "warn");
      return;
    }
    if (file.size > MAX_BYTES) {
      showToast(`El PDF pesa ${fmtMB(file.size)} MB (máximo ${MAX_MB} MB).`, "warn");
      return;
    }

    try {
      setUploadingKey(slot.key);
      const result = await uploadToCloudinary(file);

      await dispatch(
        upsertSiteDocument({
          key: slot.key,
          url: result.secure_url,
          secureUrl: result.secure_url,
          publicId: result.public_id,
          resourceType: result.resource_type,
          format: result.format,
          bytes: result.bytes,
          originalName: file.name,
        })
      );

      await dispatch(fetchSiteDocuments());
      showToast(`"${slot.label}" guardado correctamente.`, "ok");
    } catch (err) {
      console.error(err);
      showToast(err?.message || "No se pudo subir el documento.", "danger");
    } finally {
      setUploadingKey(null);
    }
  };

  const onDelete = async (doc, slot) => {
    if (
      !window.confirm(
        `¿Eliminar "${slot.label}"? El enlace del pie de página quedará vacío hasta que subas uno nuevo.`
      )
    )
      return;

    try {
      setDeletingId(doc._id);
      await dispatch(deleteSiteDocument(doc._id));
      await dispatch(fetchSiteDocuments());
      showToast("Documento eliminado.", "ok");
    } catch {
      showToast("No se pudo eliminar el documento.", "danger");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="hadacy-docs">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600&family=Jost:wght@200;300;400;500&display=swap');

        .hadacy-docs {
          --cream: #f5f0e8;
          --dark: #1a1410;
          --gold: #b8933a;
          --gold-light: #d4af6a;
          --muted: #7a6e62;
          --card-bg: #faf7f2;
          --recess: #efe7d8;
          --hairline: rgba(184, 147, 58, 0.18);
          --danger: #b04a3c;
          --success: #5b7a3c;
          font-family: "Jost", sans-serif;
          color: var(--dark);
          max-width: 900px;
          margin: 1.6rem auto 3rem;
          padding: 0 1.2rem;
        }
        .hadacy-docs__eyebrow {
          font-size: 0.66rem;
          letter-spacing: 0.42em;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 0.4rem;
        }
        .hadacy-docs__title {
          font-family: "Cormorant Garamond", serif;
          font-weight: 400;
          font-size: 2.1rem;
          line-height: 1.15;
          margin: 0 0 0.4rem 0;
        }
        .hadacy-docs__lede {
          font-size: 0.92rem;
          color: var(--muted);
          line-height: 1.55;
          max-width: 680px;
          margin: 0 0 1.6rem 0;
        }
        .hadacy-docs-card {
          background: var(--card-bg);
          border: 1px solid var(--hairline);
          border-radius: 16px;
          padding: 1.3rem 1.4rem;
          box-shadow: 0 14px 40px rgba(26, 20, 16, 0.04);
          margin-bottom: 1.2rem;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 1rem;
          justify-content: space-between;
        }
        .hadacy-docs-card__info { flex: 1 1 320px; min-width: 0; }
        .hadacy-docs-card__label {
          font-family: "Cormorant Garamond", serif;
          font-size: 1.4rem;
          margin: 0 0 0.25rem 0;
        }
        .hadacy-docs-card__where {
          font-size: 0.8rem;
          color: var(--muted);
          line-height: 1.45;
          margin: 0 0 0.4rem 0;
        }
        .hadacy-docs-card__meta {
          font-size: 0.76rem;
          color: var(--muted);
          margin: 0;
        }
        .hadacy-docs-card__meta a { color: var(--gold); text-decoration: none; }
        .hadacy-docs-card__meta a:hover { text-decoration: underline; }
        .hadacy-docs-pill {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.2rem 0.55rem;
          border-radius: 999px;
          font-size: 0.62rem;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          border: 1px solid;
          font-weight: 500;
          margin-bottom: 0.5rem;
        }
        .hadacy-docs-pill--ok {
          color: var(--success);
          border-color: rgba(91, 122, 60, 0.4);
          background: rgba(91, 122, 60, 0.08);
        }
        .hadacy-docs-pill--empty {
          color: var(--muted);
          border-color: var(--hairline);
          background: var(--recess);
        }
        .hadacy-docs-card__actions {
          display: flex;
          gap: 0.55rem;
          flex: 0 0 auto;
          flex-wrap: wrap;
        }
        .hadacy-docs-cta {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.7rem 1.3rem;
          border-radius: 999px;
          background: var(--dark);
          border: 1px solid var(--dark);
          color: var(--gold-light);
          font-size: 0.68rem;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          cursor: pointer;
          font-family: "Jost", sans-serif;
          transition: background .2s ease, color .2s ease, border-color .2s ease;
        }
        .hadacy-docs-cta:hover:not(:disabled) {
          background: var(--gold);
          border-color: var(--gold);
          color: var(--dark);
        }
        .hadacy-docs-cta:disabled { opacity: 0.65; cursor: not-allowed; }
        .hadacy-docs-cta input[type="file"] {
          position: absolute;
          inset: 0;
          opacity: 0;
          cursor: pointer;
        }
        .hadacy-docs-cta input[type="file"]:disabled { cursor: not-allowed; }
        .hadacy-docs-icon {
          width: 40px; height: 40px;
          border-radius: 999px;
          border: 1px solid var(--hairline);
          background: transparent;
          color: var(--muted);
          font-size: 0.85rem;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: background .2s ease, color .2s ease, border-color .2s ease;
        }
        .hadacy-docs-icon--danger:hover {
          background: rgba(176, 74, 60, 0.1);
          color: var(--danger);
          border-color: var(--danger);
        }
        .hadacy-docs-icon:disabled { opacity: 0.55; cursor: not-allowed; }
        .hadacy-docs-banner {
          padding: 0.7rem 0.95rem;
          border-radius: 10px;
          margin-bottom: 1rem;
          font-size: 0.86rem;
          border: 1px solid;
          display: flex;
          align-items: flex-start;
          gap: 0.55rem;
        }
        .hadacy-docs-banner--danger {
          background: rgba(176, 74, 60, 0.08);
          border-color: rgba(176, 74, 60, 0.32);
          color: var(--danger);
        }
        .hadacy-docs-banner--info {
          background: rgba(184, 147, 58, 0.10);
          border-color: rgba(184, 147, 58, 0.32);
          color: var(--gold);
        }
        .hadacy-docs-toast {
          position: fixed;
          right: 1.2rem;
          bottom: 1.2rem;
          z-index: 1080;
          background: var(--card-bg);
          border: 1px solid var(--hairline);
          border-left: 4px solid var(--gold);
          border-radius: 10px;
          padding: 0.7rem 1rem;
          font-size: 0.88rem;
          color: var(--dark);
          box-shadow: 0 14px 40px rgba(26, 20, 16, 0.14);
          max-width: 360px;
        }
        .hadacy-docs-toast--ok { border-left-color: var(--success); }
        .hadacy-docs-toast--warn { border-left-color: var(--gold); }
        .hadacy-docs-toast--danger { border-left-color: var(--danger); }
      `}</style>

      <header>
        <div className="hadacy-docs__eyebrow">HADACY · administración</div>
        <h1 className="hadacy-docs__title">Documentos legales</h1>
        <p className="hadacy-docs__lede">
          Sube el PDF de cada documento. El de Aviso de Privacidad y el de
          Términos y Condiciones se muestran a los clientes desde el pie de
          página y durante el registro. Para reemplazar uno, sube el nuevo o
          elimínalo primero.
        </p>
      </header>

      <div className="hadacy-docs-banner hadacy-docs-banner--info">
        <i className="fas fa-info-circle"></i>
        <span>
          Solo se aceptan archivos PDF (máximo {MAX_MB} MB). Subir un archivo
          reemplaza automáticamente el anterior.
        </span>
      </div>

      {error && (
        <div className="hadacy-docs-banner hadacy-docs-banner--danger">
          <i className="fas fa-exclamation-circle"></i>
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <Loader />
      ) : (
        SLOTS.map((slot) => {
          const doc = docsByKey[slot.key];
          const isFilled = !!doc;
          const isUploading = uploadingKey === slot.key;
          const isDeleting = doc && deletingId === doc._id;
          return (
            <div className="hadacy-docs-card" key={slot.key}>
              <div className="hadacy-docs-card__info">
                <span
                  className={`hadacy-docs-pill ${
                    isFilled ? "hadacy-docs-pill--ok" : "hadacy-docs-pill--empty"
                  }`}
                >
                  {isFilled ? "Configurado" : "Vacío"}
                </span>
                <h2 className="hadacy-docs-card__label">{slot.label}</h2>
                <p className="hadacy-docs-card__where">{slot.where}</p>
                {isFilled ? (
                  <p className="hadacy-docs-card__meta">
                    {doc.originalName || "documento.pdf"}
                    {doc.bytes ? ` · ${fmtMB(doc.bytes)} MB` : ""} · actualizado{" "}
                    {fmtDate(doc.updatedAt)} ·{" "}
                    <a
                      href={doc.secureUrl || doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Ver PDF
                    </a>
                  </p>
                ) : (
                  <p className="hadacy-docs-card__meta">
                    Aún no se ha subido ningún archivo.
                  </p>
                )}
              </div>

              <div className="hadacy-docs-card__actions">
                <label className="hadacy-docs-cta">
                  {isUploading ? (
                    "Subiendo…"
                  ) : (
                    <>
                      <i className="fas fa-cloud-upload-alt"></i>
                      {isFilled ? "Reemplazar" : "Subir PDF"}
                    </>
                  )}
                  <input
                    type="file"
                    accept="application/pdf"
                    disabled={isUploading}
                    onClick={(e) => {
                      e.currentTarget.value = "";
                    }}
                    onChange={(e) =>
                      onPickFile(slot, e.target.files?.[0] || null)
                    }
                  />
                </label>
                {isFilled && (
                  <button
                    type="button"
                    className="hadacy-docs-icon hadacy-docs-icon--danger"
                    onClick={() => onDelete(doc, slot)}
                    disabled={isDeleting}
                    aria-label="Eliminar"
                    title="Eliminar"
                  >
                    <i
                      className={
                        isDeleting ? "fas fa-spinner fa-spin" : "fas fa-trash"
                      }
                    ></i>
                  </button>
                )}
              </div>
            </div>
          );
        })
      )}

      {toast.show && (
        <div
          className={`hadacy-docs-toast hadacy-docs-toast--${toast.tone}`}
          role="status"
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default SiteDocumentsAdminScreen;
